import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence, progress } from "motion/react";

export function simulate(s, mar, mir, w, p, j) {

    if (p === 0) {
        return [0, 0, 0];
    }
    let t = 0;
    let machines = 0;
    // initial machines array (each element is the machine's "age" / progress)
    let m = mir === 0 ? [] : new Array(mir).fill(0);
    // per-machine CPU accounting (fixed length mar)
    const TCPU = new Array(mar).fill(0);

    // main loop while jobs remain
    while (j > 0) {
        let c = 0;

        // scaling event every w ticks
        if (t % w === 0) {
            if (m.length < mar && j > m.length) {
                if (m.length === 0) {
                    // if no machines, create one
                    m = [0];
                } else {
                    // add (len(m)*s - len(m)) new machines, up to mar
                    const toAdd = m.length * s - m.length;
                    for (let k = 0; k < toAdd; k++) {
                        m.push(0);
                        if (m.length >= mar) break;
                    }
                    if (m.length > mar) m.length = mar; // extra safety cap
                }
            }
        }

        // for each machine (in order)
        for (let i = 0; i < m.length; i++) {
            TCPU[i] += 1;

            if (m[i] === 0 && j > 0) {
                j -= 1;
            }

            m[i] += 1;

            if (m[i] > p) {
                if (j <= 0) {
                    m[i] = 0;
                    j = 0;
                } else {
                    m[i] = 1;
                    j -= 1;
                }
            }
            if (m[i] >= 1) c += 1;
        }

        machines = Math.max(machines, c);
        t += 1;
    }

    // drain phase: while machines array not empty
    while (m.length > 0) {
        // iterate backwards and splice out idle or finished machines
        for (let i = m.length - 1; i >= 0; i--) {
            if (m[i] === 0 || m[i] >= p) {
                // remove machine i (like m.pop(i) in Python)
                m.splice(i, 1);
            } else {
                m[i] += 1;
                // increment TCPU at same index (TCPU array remains fixed length)
                TCPU[i] += 1;
            }
        }
        t += 1;
    }

    return [t - 1, TCPU.reduce((a, b) => a + b, 0), machines];
}

const Simulator = () => {
    const [jobs, setJobs] = useState(50);
    const [maxParallelWorkers, setMaxParallelWorkers] = useState(4);
    const [timePerCircuit, setTimePerCircuit] = useState(10);
    const sequentialTime = useMemo(() => jobs * timePerCircuit, [jobs, timePerCircuit]);
    const [parallelTime, cpu, maxVM] = useMemo(() => simulate(2, maxParallelWorkers, 0, 30, timePerCircuit, jobs), [jobs, maxParallelWorkers, timePerCircuit]);
    const speedUp = useMemo(() => sequentialTime / parallelTime, [sequentialTime, parallelTime, timePerCircuit]);

    const [queue, setQueue] = useState([]);
    const [workers, setWorkers] = useState([]);
    const [completed, setCompleted] = useState([]);
    const [timer, setTimer] = useState(0);

    // Initialize workers
    useEffect(() => {
        const newWorkers = Array.from({ length: maxParallelWorkers }, (_, i) => ({
            id: i,
            circuit: null,
            progress: 0,
        }));
        setWorkers(newWorkers);

        const circuitIds = Array.from({ length: jobs }, (_, i) => i);
        setQueue(circuitIds);
        setCompleted([]);
        setTimer(0);
    }, [jobs, maxParallelWorkers, timePerCircuit]);

    useEffect(() => {
        const interval = setInterval(() => {
            setTimer((prev) => {
                if (prev < parallelTime) {
                    return prev + 1;
                }
                return prev;
            });
        }, 100); // Update every 100ms

        return () => clearInterval(interval);
    }, [parallelTime, timePerCircuit])

    // Animation loop
    useEffect(() => {
        let tick = 0;
        let activeWorkerCount = 1;
        if (timePerCircuit !== 0) {
            const interval = setInterval(() => {
                tick += 1;

                if (tick % 30 === 0) {
                    activeWorkerCount = Math.min(activeWorkerCount * 2, maxParallelWorkers);
                }

                setWorkers((prevWorkers) => {
                    const updatedWorkers = prevWorkers.map((worker) => {
                        // Only workers within activeWorkerCount can process
                        const isActive = worker.id < activeWorkerCount;

                        if (worker.circuit !== null && isActive) {
                            const newProgress = worker.progress + (100 / timePerCircuit);
                            if (newProgress > 100) {
                                // Circuit completed
                                setCompleted((prev) => [...prev, worker.circuit]);
                                // setTimer(tick);
                                return { ...worker, circuit: null, progress: 0 };
                            }
                            return { ...worker, progress: newProgress };
                        }
                        return worker;
                    });

                    // Assign circuits from queue to idle workers (only active ones)
                    setQueue((prevQueue) => {
                        const newQueue = [...prevQueue];
                        updatedWorkers.forEach((worker, idx) => {
                            const isActive = worker.id < activeWorkerCount;
                            if (worker.circuit === null && isActive && newQueue.length > 0) {
                                updatedWorkers[idx] = {
                                    ...worker,
                                    circuit: newQueue.shift(),
                                    progress: 1,
                                };
                            }
                        });
                        return newQueue;
                    });

                    return updatedWorkers;
                });
            }, 100); // Update every 10ms

            return () => clearInterval(interval);
        }
    }, [jobs, maxParallelWorkers, timePerCircuit]);

    return (
        <section className="section">
            <div className="container is-max-desktop">
                <div className="columns is-multiline is-vcentered p-6 is-gap-5" style={{ border: "1px solid #f3e8ff", borderRadius: "1.025rem", backgroundImage: "linear-gradient(to right bottom, #faf5ff 0%, #eff6ff 100%)" }}>
                    <div className="column">
                        <div className="mb-5">
                            <span className="tag is-medium icon-text gradient-bg has-text-white">
                                <i className="fas fa-bolt"></i>
                                <span>Performance</span>
                            </span>
                        </div>
                        <h3 className="mb-4 is-size-6">Parallel Benchmarking</h3>
                        <p className="has-text-text-60 mb-5">
                            Run multiple circuit benchmarks simultaneously with
                            our parallel processing infrastructure. Dramatically
                            reduce benchmarking time by distributing workloads.
                        </p>
                        <div className="field mb-6">
                            <label className="label">Number of configurations: <span className="is-pulled-right">{jobs}</span></label>
                            <div className="control">
                                <input
                                    className="slider"
                                    step="10"
                                    min="0"
                                    max="1000"
                                    value={jobs}
                                    onChange={(e) => setJobs(parseInt(e.target.value, 10))}
                                    type="range"
                                />
                            </div>
                        </div>
                        <div className="field mb-6">
                            <label className="label">Time per configuration: <span className="is-pulled-right">{timePerCircuit}</span></label>
                            <div className="control">
                                <input
                                    className="slider"
                                    step="5"
                                    min="0"
                                    max="500"
                                    value={timePerCircuit}
                                    onChange={(e) => setTimePerCircuit(parseInt(e.target.value, 10))}
                                    type="range"
                                />
                            </div>
                        </div>
                        <div className="field mb-6">
                            <label className="label has-text-weight-medium">Maximum Number of Parallel Workers: <span className="is-pulled-right">{maxParallelWorkers}</span></label>
                            <div className="control">
                                <input
                                    className="slider"
                                    step="1"
                                    min="1"
                                    max="16"
                                    value={maxParallelWorkers}
                                    onChange={(e) => setMaxParallelWorkers(parseInt(e.target.value, 10))}
                                    type="range"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="column has-background-white p-5" style={{ borderRadius: "1.025rem", border: "1px solid #e5e7eb" }}>
                        <p className="has-text-weight-medium mb-5">Benchmarking time comparison</p>
                        <div className="mb-5">
                            <div className="mb-2">
                                <span className="is-5 has-text-text-50">Sequential Runtime:</span>
                                <span className="has-text-weight-medium is-pulled-right">{sequentialTime.toFixed(2)}s</span>
                            </div>
                            <div className="longline">
                                <div className="longline-overlay" style={{ backgroundColor: "#99a1af" }}></div>
                            </div>
                        </div>
                        <div className="mb-5">
                            <div className="mb-2">
                                <span className="is-5 has-text-text-50">Parallel Runtime:</span>
                                <span className="has-text-weight-medium is-pulled-right">{parallelTime.toFixed(2)}s</span>
                            </div>
                            <div className="longline">
                                <div className="longline-overlay" style={{
                                    width: `${(parallelTime / sequentialTime) * 100}%`,
                                    backgroundImage: "linear-gradient(to right, #ad46ff 0%, #2b7fff 100%)"
                                }}></div>
                            </div>
                        </div>
                        <div className="has-text-centered p-5 mb-5" style={{
                            backgroundImage: "linear-gradient(to right bottom, #faf5ff 0%, #eff6ff 100%)",
                            borderRadius: "0.625rem"
                        }}>
                            <p className="is-size-5 has-text-text-50 mb-2">Speedup</p>
                            <p className="is-size-3 gradient-text">{speedUp.toFixed(2)}x faster</p>
                        </div>

                        <div className="is-size-5">
                            <div className="py-2">
                                <span className="is-size-5 has-text-tex-60">Time per job:</span>
                                <span className="is-size-5 has-text-weight-medium is-pulled-right">{timePerCircuit}s</span>
                            </div>
                            <div className="py-2">
                                <span className="is-size-5 has-text-tex-60">Total jobs:</span>
                                <span className="is-size-5 has-text-weight-medium is-pulled-right">{jobs}</span>
                            </div>
                            <div className="py-2">
                                <span className="is-size-5 has-text-tex-60">Workers utilized:</span>
                                <span className="is-size-5 has-text-weight-medium is-pulled-right">{maxVM}</span>
                            </div>
                        </div>
                    </div>

                    {/* Desktop: Animated Worker Visualization */}
                    <div className="column is-full is-hidden-touch" style={{ margin: "0" }}>
                        <div className="has-background-white p-5" style={{ border: "1px solid #e5e7eb", borderRadius: "1.025rem" }}>
                            <p className="has-text-medium mb-5">Live Processing Visualization <span className="is-pulled-right">Time: {timer}s</span></p>
                            <div className="columns ">
                                {/* Queue Section */}
                                <div className="column is-3">
                                    <div className="mb-3">
                                        <span className="is-size-6 has-text-text-50">Queue</span>
                                        <span className="tag is-medium px-2 py-1 is-pulled-right" style={{ backgroundColor: "#f3e8ff", color: "#9810fa" }}>
                                            {queue.length} waiting
                                        </span>
                                    </div>
                                    <div className="p-2 is-flex is-flex-wrap-wrap is-gap-1"
                                        style={{
                                            minHeight: "60px",
                                            backgroundColor: "#f9fafb",
                                            border: "2px dashed #e5e7eb",
                                            borderRadius: "0.625rem"
                                        }}>
                                        <AnimatePresence>
                                            {queue.slice(0, 15).map((circuitId) => (
                                                <motion.div
                                                    key={`queue-${circuitId}`}
                                                    initial={false}
                                                    animate={{ scale: 1, opacity: 1 }}
                                                    exit={{ scale: 0, opacity: 0, x: 20 }}
                                                    transition={{ duration: 0.1 }}
                                                    className="is-flex is-align-items-center is-justify-content-center has-text-white is-size-6"
                                                    style={{
                                                        width: "20%",
                                                        minHeight: "32px",
                                                        backgroundImage: "linear-gradient(to right bottom, #c27aff 0%, #51a2ff 100%)",
                                                        borderRadius: "6.25px"
                                                    }}
                                                >
                                                    {circuitId + 1}
                                                </motion.div>
                                            ))}
                                            {queue.length > 15 && (
                                                <div className="is-flex is-align-items-center is-justify-content-center has-text-text-50 is-size-6"
                                                    style={{
                                                        width: "20%",
                                                        height: "32px",
                                                        backgroundColor: "#e5e7eb",
                                                        borderRadius: "6.25px"
                                                    }}>
                                                    +{queue.length - 15}
                                                </div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </div>

                                {/* Workers Section */}
                                <div className="column is-half">
                                    <div className='mb-3'>
                                        <span className="is-size-6 has-text-text-50 mb-3">Workers</span>
                                        <span className="tag is-medium px-2 py-1 is-pulled-right" style={{ backgroundColor: "#dbeafe", color: "#155dfc" }}>
                                            {workers.filter((w) => w.circuit !== null).length} / {workers.length} active
                                        </span>
                                    </div>
                                    <div className="fixed-grid has-4-cols">
                                        <div className="grid">
                                            {workers.map((worker) => (
                                                <motion.div
                                                    key={`worker-${worker.id}`}
                                                    className="cell p-3 transition-all"
                                                    style={{
                                                        backgroundColor: `${worker.circuit !== null ? "#eff6ff" : "#f9fafb"}`,
                                                        border: `${worker.circuit !== null ? "2px solid #8ec5ff" : "2px solid #e5e7eb"}`,
                                                        borderRadius: "6.25px"
                                                    }}
                                                >
                                                    <div className="is-flex is-justify-content-space-between is-align-items-center mb-2">
                                                        <span className="is-size-7">
                                                            Worker {worker.id + 1}
                                                        </span>
                                                        {worker.circuit !== null ? (
                                                            <motion.div
                                                                animate={{ scale: [1, 1.2, 1] }}
                                                                transition={{ duration: 0.5, repeat: Infinity }}
                                                                className=""
                                                                style={{
                                                                    // width: "0.5rem",
                                                                    height: "0.5rem",
                                                                    borderRadius: "50%",
                                                                    backgroundColor: "#00c951"
                                                                }}
                                                            />
                                                        ) : (
                                                            <div className="" style={{
                                                                // width: "0.5rem",
                                                                height: "0.5rem",
                                                                borderRadius: "50%",
                                                                backgroundColor: "#d1d5dc"
                                                            }} />
                                                        )}
                                                    </div>
                                                    {worker.circuit !== null ? (
                                                        <>
                                                            <div className="is-size-7 has-text-text-60 mb-2">
                                                                Job {worker.circuit + 1}
                                                            </div>
                                                            <div className=""
                                                                style={{
                                                                    width: "100%",
                                                                    height: "0.375rem",
                                                                    backgroundColor: "#e5e7eb",
                                                                    borderRadius: "10px",
                                                                    overflow: "hidden"
                                                                }}
                                                            >
                                                                <motion.div
                                                                    className=""
                                                                    initial={{ width: 0 }}
                                                                    animate={{ width: `${worker.progress}%` }}
                                                                    transition={{ duration: 0.1 }}
                                                                    style={{
                                                                        height: "100%",
                                                                        backgroundImage: "linear-gradient(to right, #ad46ff 0%, #2b7fff 100%)"
                                                                    }}
                                                                />
                                                            </div>
                                                            <div className="is-size-7 has-text-right mt-1 has-text-text-50">
                                                                {Math.round(worker.progress)}%
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="is-size-7 has-text-text-40 has-text-centered py-2">
                                                            {workers.filter((w) => w.circuit !== null).length == 0 ? 'Offline' : 'Idle'}
                                                        </div>
                                                    )}
                                                </motion.div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Completed Section*/}
                                <div className="column is-3">
                                    <div className="mb-3">
                                        <span className="is-size-6 has-text-text-50">Completed</span>
                                        <span className="tag is-medium px-2 py-1 is-pulled-right" style={{
                                            backgroundColor: "#dcfce7",
                                            color: "#00a63e"
                                        }}>
                                            {completed.length} done
                                        </span>
                                    </div>
                                    <div className="p-3 is-flex is-flex-wrap-wrap is-gap-1"
                                        style={{
                                            minHeight: "60px",
                                            backgroundColor: "#f0fdf4",
                                            border: "2px dashed #b9f8cf",
                                            borderRadius: "0.625rem"
                                        }}>
                                        <AnimatePresence>
                                            {completed.slice(-15).reverse().map((circuitId) => (
                                                <motion.div
                                                    key={`completed-${circuitId}`}
                                                    initial={{ scale: 0, opacity: 0 }}
                                                    animate={{ scale: 1, opacity: 1 }}
                                                    className="is-flex is-align-items-center is-justify-content-center has-text-white is-size-6"
                                                    style={{
                                                        width: "20%",
                                                        height: "32px",
                                                        backgroundImage: "linear-gradient(to right bottom, #05df72 0%, #00d492 100%)",
                                                        borderRadius: "6.25px"
                                                    }}
                                                >
                                                    {circuitId + 1}
                                                </motion.div>
                                            ))}
                                            {completed.length > 15 && (
                                                <div className="is-flex is-align-items-center is-justify-content-center is-size-6"
                                                    style={{
                                                        width: "20%",
                                                        height: "32px",
                                                        backgroundColor: "#b9f8cf",
                                                        color: "#008236",
                                                        borderRadius: "6.25px"
                                                    }}>
                                                    +{completed.length - 15}
                                                </div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
};

export default Simulator;
